import { useState, useMemo, useEffect, useContext } from 'react';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import { DataSourceType } from 'automation-ui-formula-editor/data-passing-panel/types/dataSourcesTypes';
import { useGetDataSource } from 'automation-ui-formula-editor/data-sources/hooks/useGetDataSource';
import { isVisibleWhenSearchFiltered } from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/utils/isVisibleWhenSearchFiltered';
import { isPropertyTypeAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isPropertyTypeAllowListed';
import DataPanelProperty from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPanelProperty/DataPanelProperty';
import DataPanelSourceSkeleton from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPanelSourceSkeleton';
import { isDataSourceAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isDataSourceAllowListed';
import { isPropertyFieldTypeAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isPropertyFieldTypeAllowListed';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledLabel = styled.div.withConfig({
  displayName: "DataPassingPanelBodyActionOutput__StyledLabel",
  componentId: "tv8a3t-0"
})(["position:relative;"]);
const DataPassingPanelBodyActionOutput = ({
  defaultOpen,
  disabled,
  dataSourceAllowList,
  propertyFieldTypeAllowList,
  propertyTypeAllowList,
  requiredPermissionLevel,
  flowId,
  onDataPropertySelected,
  prefix,
  searchFilterString
}) => {
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const {
    data,
    loading,
    error
  } = useGetDataSource({
    prefix
  });
  const {
    dataSourceDefinition = {},
    dataSourceProperties = {}
  } = data || {};
  const {
    dataSourceType
  } = dataSourceDefinition;
  useEffect(() => {
    if (!isOpen || loading || !data || !dataSourceType) {
      return;
    }
    trackEvent(usageTracker, Trackables.VIEWED_DATA_PANEL_DATA_SOURCE, {
      screen: 'Data Passing Panel UX Revised',
      flowId,
      source: dataSourceDefinition.dataSourceType
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data, loading]);
  const dataSourceItems = useMemo(() => {
    const sourceProperties = Object.values(dataSourceProperties).filter(property => {
      return isVisibleWhenSearchFiltered({
        property,
        searchFilterString
      });
    });
    return sourceProperties.length > 0 ? sourceProperties.sort((prevProp, currProp) => prevProp.label.toLowerCase().localeCompare(currProp.label.toLowerCase())) : [];
  }, [dataSourceProperties, searchFilterString]);
  if (loading || !data) {
    return /*#__PURE__*/_jsx(DataPanelSourceSkeleton, {});
  }
  if (error) {
    return /*#__PURE__*/_jsx("div", {
      children: "Error"
    });
  }
  const {
    name,
    label
  } = dataSourceDefinition || {};
  const isDataSourceFilteredOut = !isDataSourceAllowListed({
    prefix: name,
    dataSourceAllowList
  });
  const renderAccordionContent = () => {
    if (!dataSourceProperties) {
      return null;
    }
    if (loading) {
      return /*#__PURE__*/_jsx(DataPanelSourceSkeleton, {});
    }
    if (dataSourceItems.length) {
      return dataSourceItems.map(property => /*#__PURE__*/_jsx(DataPanelProperty, {
        onDataPropertySelected: () => onDataPropertySelected(property, dataSourceDefinition, `${name}.${property.name}`),
        disabled: disabled,
        fromDataSourceName: name,
        dataProperty: property,
        isPropertyTypeCompatible: isPropertyTypeAllowListed({
          property,
          propertyTypeAllowList
        }) && isPropertyFieldTypeAllowListed({
          property,
          propertyFieldTypeAllowList,
          dataSourceType
        }),
        requiredPermissionLevel: requiredPermissionLevel,
        isDataSourceFilteredOut: isDataSourceFilteredOut
      }, `${name}.${property.name}`));
    }

    // Empty state if there are no items
    return /*#__PURE__*/_jsx(DataPanelProperty, {
      onDataPropertySelected: () => {},
      fromDataSourceName: '',
      disabled: true // keep disabled
      ,
      dataProperty: {
        name: '',
        label: searchFilterString ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-filtered-properties-found"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-properties"
        }),
        description: '',
        dataPropertyType: DataSourceType.EMPTY
      },
      isPropertyTypeCompatible: true,
      requiredPermissionLevel: "none"
    }, 'no_');
  };
  return /*#__PURE__*/_jsx(UIAccordionItem, {
    "data-test-id": `data-panel-action-output-${name}`,
    title: /*#__PURE__*/_jsx(StyledLabel, {
      children: label
    }),
    size: 'small',
    open: isOpen || !!(searchFilterString && dataSourceItems.length),
    className: "p-x-3",
    onOpenChange: () => setIsOpen(!isOpen),
    renderContent: () => renderAccordionContent()
  }, name);
};
export default DataPassingPanelBodyActionOutput;